import {t} from 'i18next';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  BaseEditor,
  BaseRange,
  createEditor,
  Descendant,
  Editor,
  Element,
  Node,
  Text,
  Transforms,
} from 'slate';
import {withHistory} from 'slate-history';
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  Slate,
  withReact,
} from 'slate-react';
import AurTooltip from '../../../../common/aur-tooltip/AurTooltip';

export interface TermData {
  value: string;
  isAPIErrorTerm?: boolean;
  isDuplicateTerm?: boolean;
  isAPIHighlightedTerm?: boolean;
}

export const extractTermData = (nodes: any[]): TermData[] => {
  return nodes.reduce<TermData[]>((acc, node) => {
    if (node.type === 'list-item' && Array.isArray(node.children)) {
      const textNode = node.children?.[0];
      const text = textNode?.text?.trimStart();

      if (text) {
        const term: TermData = {value: text};
        if (node.isAPIErrorTerm) term.isAPIErrorTerm = true;
        if (node.isDuplicateTerm) term.isDuplicateTerm = true;
        if (node.isAPIHighlightedTerm) term.isAPIHighlightedTerm = true;
        acc.push(term);
      }
    } else if (node.type === 'ordered-list' && Array.isArray(node.children)) {
      acc.push(...extractTermData(node.children));
    }
    return acc;
  }, []);
};

type ParagraphElement = {type: 'paragraph'; children: CustomText[]};
type ListElement = {type: 'ordered-list'; children: ListItemElement[]};
type ListItemElement = {
  type: 'list-item';
  isAPIErrorTerm?: boolean;
  isDuplicateTerm?: boolean;
  isAPIHighlightedTerm?: boolean;
  children: Descendant[];
};
type CustomText = {text: string};

type CustomElement = ParagraphElement | ListElement | ListItemElement;

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

interface AurSlateEditorProps {
  initialListData: TermData[];
  onChange: (listData: TermData[]) => void;
  updateHighlightedState: (value: boolean) => void;
  onErrorTermClick?: (index: number) => void;
  placeholder?: boolean;
  /**
   * This variable used here only for execute useEffect to scroll on selected api error term when needed.
   * Ticekt: https://consilio.atlassian.net/browse/ARHD-16416
   */
  focusedAPIErrorIndex?: number;
  isReadOnly?: boolean;
}

const AurSlateEditor: React.FC<AurSlateEditorProps> = ({
  initialListData,
  onChange,
  updateHighlightedState,
  onErrorTermClick,
  focusedAPIErrorIndex: scrollToTermId,
  placeholder = true,
  isReadOnly = false,
}) => {
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const [editor] = useState(() => {
    const e = withHistory(withReact(createEditor()));
    const {normalizeNode} = e;
    e.getChunkSize = (node) => (Editor.isEditor(node) ? 50 : null);
    e.normalizeNode = ([node, path]) => {
      if (
        Element.isElement(node) &&
        node.type === 'list-item' &&
        path.length === 2 &&
        node.children.length === 1 &&
        Text.isText(node.children[0]) &&
        !node.children[0].text.trim()
      ) {
        Transforms.setNodes(
          e,
          {
            isAPIErrorTerm: false,
            isDuplicateTerm: false,
            isAPIHighlightedTerm: false,
          },
          {at: path}
        );
      }
      normalizeNode([node, path]);
    };

    return e;
  });

  const isExternalUpdate = useRef(false);
  const hasTyped = useRef(false);
  const [termsCount, setTermsCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const prevSelectionRef = useRef<BaseRange | null>(null);
  const previousDataRef = useRef<string>('');
  // Store focused API error term index so determine upcoming update is for show next or previous error to be focused
  const focusedAPIErrorIndexRef = useRef<number>(0);

  const convertToSlateValue = (listData: TermData[]): Descendant[] => [
    {
      type: 'ordered-list',
      children:
        listData.length > 0
          ? listData.map((item) => ({
              type: 'list-item',
              isAPIErrorTerm: item.isAPIErrorTerm,
              isDuplicateTerm: item.isDuplicateTerm,
              isAPIHighlightedTerm: item.isAPIHighlightedTerm,
              children: [{text: item.value}],
            }))
          : [{type: 'list-item', children: [{text: ''}]}],
    },
  ];

  const extractCurrentData = (): TermData[] => {
    const result: TermData[] = [];

    editor.children.forEach((node: any) => {
      if (node.type === 'ordered-list' && Array.isArray(node.children)) {
        node.children.forEach((li: any) => {
          const textNode = li.children?.[0];
          if (textNode?.text?.trim()) {
            result.push({
              value: textNode.text,
              isAPIErrorTerm: li.isAPIErrorTerm ?? false,
              isDuplicateTerm: li.isDuplicateTerm ?? false,
              isAPIHighlightedTerm: li.isAPIHighlightedTerm ?? false,
            });
          }
        });
      }
    });

    return result;
  };
  useEffect(() => {
    const newNodes = convertToSlateValue(initialListData);
    const currentData = extractCurrentData();
    const incomingData = initialListData
      .filter((item) => item.value.trim() !== '')
      .map((item) => ({
        value: item.value,
        isAPIErrorTerm: item.isAPIErrorTerm ?? false,
        isDuplicateTerm: item.isDuplicateTerm ?? false,
        isAPIHighlightedTerm: item.isAPIHighlightedTerm ?? false,
      }));

    setTermsCount(incomingData.length);
    const hasChanged =
      JSON.stringify(currentData) !== JSON.stringify(incomingData);
    if (!hasChanged) return;
    isExternalUpdate.current = true;
    Transforms.select(editor, {path: [0, 0], offset: 0});
    if (editor.children.length > 0) {
      Transforms.removeNodes(editor, {at: [0]});
    }
    Transforms.insertNodes(editor, newNodes, {at: [0]});

    if (
      prevSelectionRef?.current &&
      Node.has(editor, prevSelectionRef.current.anchor.path)
    ) {
      Transforms.select(editor, prevSelectionRef.current);
      ReactEditor.focus(editor);
      prevSelectionRef.current = null;
    }
  }, [initialListData]);

  useEffect(() => {
    if (!scrollToTermId) return;
    const isBackPressed = focusedAPIErrorIndexRef.current > scrollToTermId;
    focusedAPIErrorIndexRef.current = scrollToTermId;
    const container = editorContainerRef.current;
    if (!container) return;

    /**
     * Finding current selected api error element so we can calculte scrolling is required or not based on its position
     * highlight-api-error-selection class only apply to single term at time which is current focused so its unique.
     */
    const element = container.querySelector<HTMLElement>(
      'li.highlight-api-error-selection'
    );

    if (element) {
      // Element position relative to the container's scrollable content:
      const cRect = container.getBoundingClientRect();
      const eRect = element.getBoundingClientRect();
      // Setup margin so when element visble by scroll on click of button then its not show at bottom without any space between element and bottom edge
      const margin = 10;
      // Considering top and bottom padding of scroll container to calculate required top to scroll for make element visble
      const paddingTop = parseInt(getComputedStyle(container).paddingTop) || 0;
      const paddingBottom =
        parseInt(getComputedStyle(container).paddingBottom) || 0;
      const currentTop = container.scrollTop;

      /**
       * Getting element top position from container's top and its current scroll position
       * For example, current Container height is 400 and total scorllable content is 800 and scroll position of container is 0 so total content from postion 0 to 400(container height) is visible
       * term element is located at y = 500 and its height is 20.
       * So now if we went to make element visible in container when user click on next button then we neen to set Scroll to Top = 120 so element will be visible at
       * bottom by this. If scroll To Top set 120 then total area visible is not top 120 + 400 (Container height) = 520 and element's Y 500 is now it will be visible
       * in container as we scroll container to 120 px from top
       */
      const elementTopInContainer = eRect.top - cRect.top + currentTop;
      const elementBottomInContainer =
        elementTopInContainer + element.offsetHeight;

      const visibleTop = currentTop + paddingTop + margin;
      const visibleBottom =
        currentTop +
        (isBackPressed ? eRect.height : 0) +
        container.clientHeight -
        paddingBottom -
        margin;

      // If element is already fully visible, do nothing. When user click on button and element already displayed in visible aread of container then no need to scroll
      if (
        elementTopInContainer >= visibleTop &&
        elementBottomInContainer <= visibleBottom
      ) {
        return; // no scroll needed
      }

      // If element is taller than container (can't be fully visible),
      // align its top (you can change to align bottom or center if preferred)
      if (element.offsetHeight > container.clientHeight - 2 * margin) {
        const targetTop = Math.max(
          0,
          Math.min(
            elementTopInContainer - margin,
            container.scrollHeight - container.clientHeight
          )
        );
        container.scrollTo({top: targetTop, behavior: 'auto'});
        return;
      }

      // If above visible area → scroll up to make top visible
      if (elementTopInContainer < visibleTop) {
        const targetTop = elementTopInContainer - paddingTop - margin;
        container.scrollTo({top: targetTop, behavior: 'auto'});
        return;
      }

      // If below visible area → scroll down to make bottom visible
      if (elementBottomInContainer > visibleBottom) {
        const targetTop =
          elementBottomInContainer -
          container.clientHeight +
          paddingBottom +
          margin;
        container.scrollTo({top: targetTop, behavior: 'auto'});
        return;
      }
    }
  }, [scrollToTermId]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const extractListTexts = (nodes: any[]): string[] => {
    let result: string[] = [];
    nodes.forEach((node) => {
      if (node.type === 'list-item' && Array.isArray(node.children)) {
        node.children.forEach((child: any) => {
          if (child.text) {
            result.push(child.text);
          }
        });
      } else if (node.type === 'ordered-list' && Array.isArray(node.children)) {
        result = result.concat(extractListTexts(node.children));
      }
    });
    return result;
  };

  const handleEditorDataChange = (value: any[]) => {
    if (isExternalUpdate.current) {
      isExternalUpdate.current = false;
      return;
    }
    const formattedArray: TermData[] = extractTermData(value);
    const currentDataString = JSON.stringify(formattedArray);
    if (currentDataString !== previousDataRef.current) {
      previousDataRef.current = currentDataString;
      onChange(formattedArray);
    }
    if (hasTyped.current) {
      updateHighlightedState(false);
      hasTyped.current = false;
    }
  };

  const renderElement = useCallback(
    ({element, children, attributes}: RenderElementProps) => {
      const slateElement = element as ListItemElement;

      if (slateElement.type === 'list-item') {
        const isError = slateElement.isAPIErrorTerm;
        const isDuplicate = slateElement.isDuplicateTerm;
        const isHighlighted = slateElement.isAPIHighlightedTerm;

        const tooltipText = isError
          ? t('eca.searchTermReport.tiptapeditor.invalidTerm')
          : t('eca.searchTermReport.tiptapeditor.duplicateTerm');

        const highlightClass = isError
          ? 'highlight-api-error'
          : isDuplicate
            ? 'highlight-duplicates'
            : '';
        const handleClick = () => {
          if ((isError || isHighlighted) && onErrorTermClick) {
            const selection = editor.selection;
            if (selection) {
              prevSelectionRef.current = selection;
            }
            const currentNodePath = ReactEditor.findPath(editor, element);

            const errorTermsWithPaths: {value: string; path: number[]}[] = [];

            editor.children.forEach((node: any) => {
              if (node.type === 'ordered-list') {
                node.children.forEach((li: any, index: number) => {
                  if (li.isAPIErrorTerm) {
                    const val = li.children?.[0]?.text;
                    if (val?.trim()) {
                      errorTermsWithPaths.push({
                        value: val,
                        path: [0, index],
                      });
                    }
                  }
                });
              }
            });

            const index =
              errorTermsWithPaths.findIndex(
                (t) =>
                  JSON.stringify(t.path) === JSON.stringify(currentNodePath)
              ) + 1;

            if (index > 0) {
              onErrorTermClick(index);
            }
          }
        };

        return (
          <li
            {...attributes}
            className={`${highlightClass} ${slateElement.isAPIHighlightedTerm ? 'highlight-api-error-selection' : ''}`}
            onClick={handleClick}
          >
            {isError || isDuplicate ? (
              <AurTooltip
                title={tooltipText}
                placement="top"
                tooltipSx={{
                  maxWidth: '195px',
                  fontSize: '12px',
                  fontWeight: '600',
                  fontFamily: 'Inter',
                  lineHeight: '18px',
                  letterSpacing: '0.25px',
                }}
              >
                <span className="body3_size_custom">{children}</span>
              </AurTooltip>
            ) : (
              children
            )}
          </li>
        );
      }

      if (slateElement.type === 'ordered-list') {
        return <ol {...attributes}>{children}</ol>;
      }

      return <p {...attributes}>{children}</p>;
    },
    []
  );

  const getPaddingLeft = (listSize: number): number => {
    const digits = listSize.toString().length;
    return 11 + digits * 5;
  };

  return (
    <Slate
      editor={editor}
      initialValue={convertToSlateValue(initialListData)}
      onChange={handleEditorDataChange}
    >
      <div
        className="editor-container editor-container-small-padding"
        style={{paddingLeft: getPaddingLeft(termsCount)}}
        ref={editorContainerRef}
        tabIndex={-1}
        onFocus={(e) => {
          if (e.target === editorContainerRef.current) {
            setIsFocused(true);
            ReactEditor.focus(editor);
          }
        }}
      >
        <Editable
          aria-label={t('eca.searchTermReport.tiptapeditor.editorName')}
          aria-readonly={isReadOnly}
          readOnly={isReadOnly}
          onKeyDown={() => {
            hasTyped.current = true;
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`editor-input typo-text body3_size`}
          placeholder={
            !isFocused && placeholder
              ? 'Enter search terms or statements...'
              : ''
          }
          renderPlaceholder={(props) => {
            console.log({props});
            return (
              <div
                {...props}
                style={{
                  ...props.attributes.style,
                  padding: '0px',
                }}
                contentEditable={false}
                className="editor-placeholder"
              />
            );
          }}
          renderElement={renderElement}
        />
      </div>
    </Slate>
  );
};

export default AurSlateEditor;
